import React, {createContext, useContext, useState, useEffect, useCallback} from 'react';
import {fetchData} from '../AxiosRequest/axios';

const DataContext = createContext();

export const DataProvider = ({children}) => {
    const CACHE_DURATION = 5 * 60 * 1000;

    const [cache, setCache] = useState(() => {
        const saved = sessionStorage.getItem('dataCache');
        if (!saved) return {};

        const parsed = JSON.parse(saved);
        const now = Date.now();


        const validCache = {};
        Object.keys(parsed).forEach(key => {
            if (parsed[key].timestamp && (now - parsed[key].timestamp < CACHE_DURATION)) {
                validCache[key] = parsed[key];
            }
        });

        return validCache;
    });

    const [loading, setLoading] = useState({});
    const [error, setError] = useState({});

    const load = useCallback(async (key, endpoint, options = {}) => {
        if (cache[key]) return;

        setLoading(prev => ({...prev, [key]: true}));
        setError(prev => ({...prev, [key]: null}));

        try {
            const lang = localStorage.getItem('language') || 'en';
            const data = await fetchData({endpoint, lang, ...options});
            setCache(prev => {
                const updated = {...prev, [key]: data};
                sessionStorage.setItem('dataCache', JSON.stringify(updated));
                return updated;
            });
        } catch (e) {
            setError(prev => ({...prev, [key]: e.message}));
        } finally {
            setLoading(prev => ({...prev, [key]: false}));
        }
    }, [cache]);


    const invalidateCache = useCallback((key) => {
        if (key) {
            // Invalidate specific key
            setCache(prev => {
                const updated = {...prev};
                delete updated[key];
                sessionStorage.setItem('dataCache', JSON.stringify(updated));
                return updated;
            });
        } else {
            // Invalidate all cache
            setCache({});
            sessionStorage.removeItem('dataCache');
        }
    }, []);


    const reload = useCallback(async (key, endpoint, options = {}) => {
        invalidateCache(key);
        setLoading(prev => ({...prev, [key]: true}));
        setError(prev => ({...prev, [key]: null}));

        try {
            const lang = localStorage.getItem('language') || 'en';
            const data = await fetchData({endpoint, lang, ...options});
            setCache(prev => {
                const updated = {
                    ...prev,
                    [key]: {
                        data,
                        timestamp: Date.now()
                    }
                };
                sessionStorage.setItem('dataCache', JSON.stringify(updated));
                return updated;
            });
        } catch (e) {
            setError(prev => ({...prev, [key]: e.message}));
        } finally {
            setLoading(prev => ({...prev, [key]: false}));
        }
    }, [invalidateCache]);


    const isCacheValid = useCallback((key) => {
        if (!cache[key]) return false;
        if (!cache[key].timestamp) return true; // Old cache format, consider valid
        return (Date.now() - cache[key].timestamp) < CACHE_DURATION;
    }, [cache, CACHE_DURATION]);

    return (
        <DataContext.Provider value={{cache, load, reload, invalidateCache, loading, error}}>
            {children}
        </DataContext.Provider>
    );
};

export const useData = () => useContext(DataContext);
